/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../../../chunks/tslib.es6.js";
import t from "../../../Color.js";
import "../../../intl.js";
import { addTokenParameter as s } from "../../../kernel.js";
import l from "../../../request.js";
import "../../../symbols.js";
import i from "../../../core/Accessor.js";
import { isSome as r } from "../../../core/arrayUtils.js";
import n from "../../../core/Collection.js";
import { JSONMap as a } from "../../../core/jsonMap.js";
import o from "../../../core/Logger.js";
import { debounce as u } from "../../../core/promiseUtils.js";
import { on as c, watch as d, whenOnce as y, initial as h } from "../../../core/reactiveUtils.js";
import { px2pt as m } from "../../../core/screenUtils.js";
import { millisecondsPerTimeUnit as f } from "../../../core/timeUtils.js";
import { addQueryParameters as p } from "../../../core/urlUtils.js";
import { property as g } from "../../../core/accessorSupport/decorators/property.js";
import "../../../core/accessorSupport/ensureType.js";
import "../../../core/has.js";
import { subclass as b } from "../../../core/accessorSupport/decorators/subclass.js";
import { OriginId as S } from "../../../core/accessorSupport/PropertyOrigin.js";
import { EffectView as _ } from "../../../layers/effects/EffectView.js";
import { effectFunctionsFromJSON as w } from "../../../layers/effects/jsonUtils.js";
import { ExportImageParameters as v } from "../../../layers/support/ExportImageParameters.js";
import { isTimeOnlyField as L } from "../../../layers/support/fieldUtils.js";
import { isFeatureLayer as E } from "../../../layers/support/layerUtils.js";
import { getPixelValueRange as C } from "../../../layers/support/rasterFormats/pixelRangeUtils.js";
import { fromJSON as I } from "../../../renderers/support/jsonUtils.js";
import { isSupportedRenderer3D as R } from "../../../renderers/support/rendererConversion.js";
import F from "../../../renderers/visualVariables/support/SizeVariableLegendOptions.js";
import { isAnyDateField as V } from "../../../smartMapping/support/utils.js";
import { applyCIMSymbolColor as T } from "../../../symbols/support/cimSymbolUtils.js";
import { SymbolSizeDefaults as z } from "../../../symbols/support/previewUtils.js";
import { renderSymbol as D } from "../../../symbols/support/renderUtils.js";
import { renderColorRampPreviewHTML as x, renderDotDensityPreviewHTML as j, renderPieChartPreviewHTML as O, renderPreviewHTML as P } from "../../../symbols/support/symbolUtils.js";
import { isVolumetricSymbol as A } from "../../../symbols/support/utils.js";
import { getClusterSizeVariable as M } from "./clusterUtils.js";
import { getColorFromPointCloudStops as U, getRampStopsForPointCloud as k, getStrectchRampStops as B, getRampStops as N } from "./colorRampUtils.js";
import { getHeatmapRampStops as q } from "./heatmapRampUtils.js";
import { getRotationAngleForFocus as H, getRelationshipRampElement as $ } from "./relationshipRampUtils.js";
import { getRampStops as W, realWorldMaxSize as G, realWorldMinSize as J } from "./sizeRampUtils.js";
import { specialCharsLessThan as Q, specialCharsGreaterThan as Z, getSymbolForFlowRenderer as K, isRasterBandStatistics as X, getMedianColor as Y, rgbImgSource as ee } from "./utils.js";
import { formatNumberLabel as te } from "../../smartMapping/support/utils.js";
import se from "../../../symbols/SimpleMarkerSymbol.js";
import le from "../../../symbols/SimpleFillSymbol.js";
import { fetchMessageBundle as ie } from "../../../intl/messages.js";
import { substitute as re } from "../../../intl/substitute.js";
const ne = "https://utility.arcgis.com/sharing/tools/legend",
  ae = "esri.layers.ImageryLayer",
  oe = "esri.layers.ImageryTileLayer",
  ue = "esri.layers.WCSLayer",
  ce = /^\s*(return\s+)?\$view\.scale\s*(;)?\s*$/i,
  de = new a({
    esriGeometryPoint: "point",
    esriGeometryMultipoint: "multipoint",
    esriGeometryPolyline: "polyline",
    esriGeometryPolygon: "polygon",
    esriGeometryMultiPatch: "multipatch"
  }),
  ye = new se({
    size: 6,
    outline: {
      color: [128, 128, 128, .5],
      width: .5
    }
  }),
  he = new le({
    style: "solid"
  });
function me(e) {
  return "flow" === e.type;
}
function fe(e) {
  return "vector-field" === e.type;
}
function pe(e) {
  return "raster-colormap" === e.type;
}
function ge(e) {
  return "raster-stretch" === e.type;
}
function be(e) {
  return "raster-shaded-relief" === e.type;
}
function Se(e) {
  return "esri.renderers.SimpleRenderer" === e.declaredClass;
}
function _e(e) {
  return "esri.renderers.ClassBreaksRenderer" === e.declaredClass;
}
function we(e) {
  return "esri.renderers.UniqueValueRenderer" === e.declaredClass;
}
function ve(e) {
  return "esri.renderers.HeatmapRenderer" === e.declaredClass;
}
function Le(e) {
  return Ce(e) || Ie(e) || Re(e) || Ee(e);
}
function Ee(e) {
  return "esri.renderers.PointCloudRGBRenderer" === e.declaredClass;
}
function Ce(e) {
  return "esri.renderers.PointCloudClassBreaksRenderer" === e.declaredClass;
}
function Ie(e) {
  return "esri.renderers.PointCloudStretchRenderer" === e.declaredClass;
}
function Re(e) {
  return "esri.renderers.PointCloudUniqueValueRenderer" === e.declaredClass;
}
function Fe(e) {
  return "esri.renderers.DotDensityRenderer" === e.declaredClass;
}
function Ve(e) {
  return "esri.renderers.PieChartRenderer" === e.declaredClass;
}
function Te(e, t) {
  return Se(e) || _e(e) || we(e) || ve(e) || Fe(e) || Ve(e) ? "2d" === t.type || R(e) : ge(e) || pe(e) || be(e) || Ce(e) || Ie(e) || Re(e) || fe(e) || me(e);
}
function ze(e) {
  return "esri.layers.BuildingSceneLayer" === e.declaredClass;
}
function De(e) {
  return "esri.layers.SubtypeGroupLayer" === e.declaredClass;
}
function xe(e) {
  return "esri.layers.VoxelLayer" === e.declaredClass;
}
function je(e) {
  return "esri.layers.WMSLayer" === e.declaredClass;
}
function Oe(e) {
  return "esri.layers.WMTSLayer" === e.declaredClass;
}
function Pe(e) {
  return "esri.layers.MapImageLayer" === e.declaredClass;
}
function Ae(e) {
  return "esri.layers.TileLayer" === e.declaredClass;
}
function Me(e) {
  return e.declaredClass === ae;
}
function Ue(e) {
  return e.declaredClass === oe;
}
function ke(e) {
  return e.declaredClass === ue;
}
function Be(e) {
  return "stretch-ramp" === e.type;
}
function Ne(e) {
  const t = "authoringInfo" in e ? e?.authoringInfo : null;
  return "univariate-color-size" === t?.type;
}
function qe(e) {
  const t = "authoringInfo" in e ? e?.authoringInfo : null;
  return "univariate-color-size" === t?.type && "above-and-below" === t?.univariateTheme;
}
function He(e) {
  return "sublayers" in e;
}
async function $e(e, t) {
  const s = await ie("esri/widgets/Legend/t9n/Legend");
  return "previewTemplateAriaLabel" !== e || t || (e = "previewAriaLabel"), re(s[e], {
    label: t
  });
}
const We = new se({
  style: "path",
  path: "M10,5 L5,0 0,5 M5,0 L5,15",
  size: 15,
  outline: {
    width: 1,
    color: [85, 85, 85, 1]
  }
});
let Ge = {},
  Je = class extends i {
    constructor(e) {
      super(e), this._hasColorRamp = !1, this._hasOpacityRamp = !1, this._hasSizeRamp = !1, this._webStyleSymbolCache = new Map(), this._dotDensityUrlCache = new Map(), this._scaleDrivenSizeVariable = null, this._hasClusterSizeVariable = !1, this.children = new n(), this.layerView = null, this.layer = null, this.legendElements = [], this.parent = null, this.hideLayersNotInCurrentView = !1, this.keepCacheOnDestroy = !1, this.respectLayerVisibility = !0, this.sublayerIds = [], this.title = null, this.view = null;
    }
    initialize() {
      const e = () => this.notifyChange("ready");
      this.addHandles([c(() => this.children, "change", t => {
        const {
          added: s,
          removed: l
        } = t;
        s.forEach(t => {
          const s = `activeLayerInfo-ready-watcher-${t.layer.uid}`;
          this.addHandles(d(() => t.ready, e, h), s);
        }), l.forEach(e => this.removeHandles(e.layer.uid)), e();
      })]), this.keepCacheOnDestroy || (Ge = {});
    }
    destroy() {
      this._webStyleSymbolCache = null, this._dotDensityUrlCache = null, this._scaleDrivenSizeVariable = null, this.keepCacheOnDestroy || (Ge = null);
    }
    get effectList() {
      const e = this.layer;
      let t = null;
      return "effect" in e && e.effect && (t = new _(), t.effect = e.effect, t.endTransitions(), t.scale = this.scale), t;
    }
    get opacity() {
      const e = this.layer.opacity,
        t = this.parent?.opacity,
        s = this.layer.parent,
        l = s && "uid" in s ? this._getParentLayerOpacity(s) : null;
      return null != t ? t * e : null != l ? l * e : e;
    }
    get ready() {
      return null === this.layer || (this.children.length > 0 ? this._isGroupActive() : this.legendElements.length > 0);
    }
    get scale() {
      return this.view?.scale ?? 0;
    }
    get isScaleDriven() {
      const e = this.layer;
      if (null === e) return !1;
      if ("effect" in e && e.effect && Array.isArray(e.effect)) return !0;
      if ("featureReduction" in e && e.featureReduction) {
        if ("cluster" === e.featureReduction.type) return !0;
        if ("binning" === e.featureReduction.type && "renderer" in e.featureReduction && e.featureReduction.renderer) return this._isRendererScaleDriven(e.featureReduction.renderer);
      }
      return "renderer" in e && e.renderer ? this._isRendererScaleDriven(e.renderer) : this._isLayerScaleDriven(this.layer);
    }
    get version() {
      return this._get("version") + 1;
    }
    async buildLegendElementsForFeatureCollections(e) {
      if (!(!this.hideLayersNotInCurrentView || (await this._isLayerInCurrentView()))) return this.legendElements = [], void this.notifyChange("ready");
      const t = Array.from(e, e => {
        if (E(e)) return this._getRendererLegendElements(e.renderer, {
          title: e.title
        });
        if (e.featureSet?.features.length) {
          const t = e.layerDefinition,
            s = t?.drawingInfo,
            l = s && I(s.renderer),
            i = de.read(t.geometryType);
          return l ? this._getRendererLegendElements(l, {
            title: e.name,
            geometryType: i
          }) : (o.getLogger(this).warn("drawingInfo not available!"), null);
        }
        return null;
      });
      try {
        const e = [],
          s = await Promise.allSettled(t);
        for (const t of s) if ("fulfilled" === t.status) for (const s of t.value ?? []) e.push(s);
        this.legendElements = e, this.notifyChange("ready");
      } catch (s) {
        o.getLogger(this).warn("error while building legend for layer!", s);
      }
    }
    async buildLegendElementsForRenderer(e) {
      try {
        const t = !this.hideLayersNotInCurrentView || (await this._isLayerInCurrentView());
        this.legendElements = t ? await this._getRendererLegendElements(e) : [], this.notifyChange("ready");
      } catch (t) {
        o.getLogger(this).warn("error while building legend for layer!", t);
      }
    }
    async buildLegendElementsForFeatureReduction(e) {
      try {
        const t = !this.hideLayersNotInCurrentView || (await this._isLayerInCurrentView());
        this.legendElements = t ? await this._getLegendElementsForFeatureReduction(e) : [], this.notifyChange("ready");
      } catch (t) {
        o.getLogger(this).warn("error while building legend for layer!", t);
      }
    }
    async buildLegendElementsForTools() {
      const e = this.layer;
      if (xe(e)) this._constructLegendElementsForVoxellayer();else if (Oe(e)) this._constructLegendElementsForWMTSlayer();else if (je(e)) await this._constructLegendElementsForWMSSublayers();else if (ze(e)) await this._constructLegendElementsForBuildingSceneLayer();else if (Pe(e) || Ae(e) || De(e)) await this._constructLegendElementsForSublayers();else {
        this.removeHandles("imageryLayers-watcher");
        let t = "default";
        if (Me(e)) {
          const s = e;
          t = (s?.rasterFunction?.functionName || "default") + "_" + (e.bandIds?.length ? e.bandIds.join("") : "###");
        }
        await this._getLegendLayers(`${e.uid}-${t}`).then(async t => {
          this.legendElements = [], this.notifyChange("ready");
          const s = t.map(async t => {
            if (Me(e) || Ue(e)) {
              const t = d(() => ["renderingRule" in e && e.rasterFunction, e.bandIds], () => u(async () => {
                Ge.default = null, e.renderer ? await this.buildLegendElementsForRenderer(e.renderer) : await this.buildLegendElementsForTools();
              })());
              this.addHandles(t, "imageryLayers-watcher");
            }
            const s = this._generateSymbolTableElementForLegendLayer(t);
            s && s.infos.length && (Me(e) && (s.title = e.title), this.legendElements.push(s)), this.notifyChange("ready");
          });
          await Promise.allSettled(s);
        }).catch(e => {
          o.getLogger(this).warn("Request to server for legend has failed!", e);
        });
      }
    }
    async _isLayerInCurrentView() {
      const e = this.layer,
        t = this.layerView,
        s = t && "createQuery" in t && "queryFeatureCount" in t;
      if (!s && !(t && "createQuery" in e && "queryFeatureCount" in e)) return !0;
      await y(() => !t.updating);
      const l = s ? "createQuery" in t && t.createQuery() : "createQuery" in e && e.createQuery();
      if (!l) return !0;
      l.geometry = this.view.extent;
      return 0 !== (s ? "queryFeatureCount" in t && (await t.queryFeatureCount(l)) : "queryFeatureCount" in e && (await e.queryFeatureCount(l)));
    }
    _getParentLayerOpacity(e) {
      let t = 1;
      const s = e.parent;
      return s && "uid" in s && (t = this._getParentLayerOpacity(s)), e.opacity * t;
    }
    _isGroupActive() {
      const e = this.children;
      return !!e.length && e.some(e => e.ready);
    }
    _isRendererScaleDriven(e) {
      if ("dot-density" === e.type) return !0;
      const t = "valueExpression" in e ? e.valueExpression : null;
      if (ce.test(t)) return !0;
      const s = "visualVariables" in e ? e.visualVariables : null;
      return !!s?.some(e => this._isScaleDrivenSizeVariable(e)) || this._hasScaleDrivenSymbols(e);
    }
    _hasScaleDrivenSymbols(e) {
      switch (e.type) {
        case "simple":
          return this._isScaleDrivenSymbol(e.symbol);
        case "class-breaks":
          return this._isScaleDrivenSymbol(e.defaultSymbol) || e.classBreakInfos.some(e => this._isScaleDrivenSymbol(e.symbol));
        case "unique-value":
          return this._isScaleDrivenSymbol(e.defaultSymbol) || !!e.uniqueValueInfos?.some(e => this._isScaleDrivenSymbol(e.symbol));
      }
      return !1;
    }
    _isScaleDrivenSymbol(e) {
      if ("cim" === e?.type) {
        const {
            primitiveOverrides: t,
            minScale: s,
            maxScale: l
          } = e.data,
          i = t?.some(e => /\$view\.scale/.test(e.valueExpressionInfo?.expression || "")) ?? !1;
        return null != s || null != l || i;
      }
      return !1;
    }
    _isScaleDrivenSizeVariable(e) {
      if (e && "size" !== e.type) return !1;
      const t = e,
        s = t.minSize,
        l = t.maxSize;
      return !("object" != typeof s || !s || !this._isScaleDrivenSizeVariable(s)) || !("object" != typeof l || !l || !this._isScaleDrivenSizeVariable(l)) || !!t.expression || ce.test(t.valueExpression);
    }
    _isLayerScaleDriven(e) {
      if ("minScale" in e && e.minScale > 0 || "maxScale" in e && e.maxScale > 0) return !0;
      if ("sublayers" in e && e.sublayers) return e.sublayers.some(e => this._isLayerScaleDriven(e));
      const t = e.parent;
      if (!1 === e.loaded && t && Pe(t) && "source" in e && e.source && "map-layer" === e.source.type) for (const s of t.sourceJSON.layers ?? []) if (s.id === e.source.mapLayerId && (s.minScale > 0 || s.maxScale > 0)) return !0;
      return !1;
    }
    async _constructLegendElementsForVoxellayer() {
      this.legendElements = [], this.removeHandles("voxel-style-watcher"), this.removeHandles("voxel-current-variable");
      const e = this.layer;
      this.addHandles(d(() => e.currentVariableId, () => this._constructLegendElementsForVoxellayer()), "voxel-current-variable"), this.addHandles(d(() => e.getVariableStyles(), () => this._constructLegendElementsForVoxellayer()), "voxel-style-watcher");
      const t = e.getVariableStyle(null),
        s = [];
      if (t) if (t.uniqueValues?.length) {
        const e = [];
        t.uniqueValues.forEach(t => {
          t.enabled && e.push({
            label: t.label || `${t.value}`,
            value: t.value,
            symbol: new le({
              color: t.color,
              outline: null
            })
          });
        }), e.length && s.push({
          type: "symbol-table",
          title: t.label,
          infos: e
        });
      } else if (t.transferFunction) {
        const {
            colorStops: e,
            stretchRange: l
          } = t.transferFunction,
          i = e.toArray().reverse(),
          r = l.map((e, t) => `${0 === t ? Q : Z} ${te(e)}`).reverse(),
          n = i.map(e => ({
            color: e.color,
            value: null,
            label: null
          }));
        n[0].label = r[0], n[n.length - 1].label = r[1], s.push({
          type: "color-ramp",
          title: t.label,
          infos: n,
          preview: x(i.map(e => e.color), {
            ariaLabel: await $e("previewColorRampAriaLabel")
          })
        });
      }
      const l = e.opacity,
        i = s.reduce((e, t) => [...e, ...this._getAllInfos(t)], []).filter(e => !!e?.symbol).map(e => this._getSymbolPreview(e, l));
      await Promise.allSettled(i), this.legendElements = s, this.notifyChange("ready");
    }
    _constructLegendElementsForWMTSlayer() {
      this.legendElements = [], this.removeHandles("wmts-activeLayer-watcher");
      const e = this.layer.activeLayer;
      if (this.addHandles(d(() => {
        const {
          layer: e
        } = this;
        return e && "activeLayer" in e && e.activeLayer;
      }, () => this._constructLegendElementsForWMTSlayer()), "wmts-activeLayer-watcher"), e.styleId && e.styles) {
        let t = null;
        e.styles.some(s => e.styleId === s.id && (t = s.legendUrl, !0)), t && (this.legendElements = [{
          type: "symbol-table",
          title: e.title,
          infos: [{
            src: t,
            opacity: this.opacity
          }]
        }]);
      }
      this.notifyChange("ready");
    }
    async _constructLegendElementsForWMSSublayers() {
      this.legendElements = [], this.removeHandles("wms-sublayers-watcher");
      const e = this.layer;
      let t = null;
      (e.customParameters || e.customLayerParameters) && (t = {
        ...e.customParameters,
        ...e.customLayerParameters
      }), this.addHandles(d(() => {
        const {
          layer: e
        } = this;
        return e && "sublayers" in e && e.sublayers;
      }, () => this._constructLegendElementsForWMSSublayers()), "wms-sublayers-watcher"), this.legendElements = await this._generateLegendElementsForWMSSublayers(e.sublayers, t), this.notifyChange("ready");
    }
    async _generateLegendElementsForWMSSublayers(e, t) {
      const s = [];
      this.addHandles(e.on("change", () => this._constructLegendElementsForWMSSublayers()), "wms-sublayers-watcher");
      const l = e.toArray();
      for (const i of l) {
        const e = d(() => [i.title, i.visible, i.legendEnabled], () => this._constructLegendElementsForWMSSublayers());
        if (this.addHandles(e, "wms-sublayers-watcher"), !this.respectLayerVisibility || i.visible && i.legendEnabled) {
          const e = await this._generateSymbolTableElementForWMSSublayer(i, t);
          e?.infos.length && s.unshift(e);
        }
      }
      return s;
    }
    async _generateSymbolTableElementForWMSSublayer(e, t) {
      if (!e.legendUrl && e.sublayers) {
        const s = (await this._generateLegendElementsForWMSSublayers(e.sublayers, t)).filter(e => e);
        return {
          type: "symbol-table",
          title: e.title,
          infos: s
        };
      }
      return this._generateSymbolTableElementForLegendUrl(e, t);
    }
    async _generateSymbolTableElementForLegendUrl(e, t) {
      let s = e.legendUrl;
      if (!s) return;
      const i = {
        type: "symbol-table",
        title: e.title || e.name || String(e.id ?? ""),
        infos: []
      };
      t && (s = p(s, t));
      let r = null;
      const n = e.layer?.opacity;
      try {
        r = (await l(s, {
          responseType: "image"
        })).data, r && (r.style.opacity = n);
      } catch {}
      return i.infos.push({
        src: s,
        preview: r,
        opacity: n
      }), i;
    }
    _getLegendLayers(e, t) {
      const s = Ge && Ge[e];
      return s ? Promise.resolve(s) : this._legendRequest(t).then(t => {
        const s = t.layers;
        return Ge[e] = s, s;
      });
    }
    _legendRequest(e) {
      const t = this.layer;
      let s = {
        f: "json",
        dynamicLayers: e
      };
      if (Me(t)) {
        const e = t.exportImageServiceParameters.rasterFunction;
        if (e && (s.renderingRule = JSON.stringify(e.functionDefinition?.toJSON() || e.toJSON())), t.bandIds && (s.bandIds = t.bandIds.join()), t.raster || t.viewId || t.customParameters) {
          const {
            raster: e,
            viewId: l,
            customParameters: i
          } = t;
          s = {
            raster: e,
            viewId: l,
            ...s,
            ...i
          };
        }
      }
      let i = t.url.replace(/(\/)+$/, "");
      if ("version" in t && +t.version >= 10.01) {
        const e = i.indexOf("?");
        e > -1 ? i = i.substring(0, e) + "/legend" + i.substring(e) : i += "/legend";
      } else {
        const e = i.toLowerCase().indexOf("/rest/"),
          t = i.substring(0, e) + i.substring(e + 5, i.length);
        i = ne + "?soapUrl=" + encodeURI(t) + "&returnbytes=true";
      }
      return l(i, {
        query: s
      }).then(e => e.data);
    }
    async _constructLegendElementsForBuildingSceneLayer() {
      this.legendElements = [], this.removeHandles("sublayers-watcher");
      const e = this.layer;
      this.addHandles(d(() => e.sublayers, () => this._constructLegendElementsForBuildingSceneLayer()), "sublayers-watcher");
      try {
        this.legendElements = await this._generateLegendElementsForBuildingSublayers(e.sublayers, this.opacity), this.notifyChange("ready");
      } catch (t) {
        o.getLogger(this).warn("Request to server for legend has failed!", t);
      }
    }
    async _generateLegendElementsForBuildingSublayers(e, t) {
      let s = [];
      this.addHandles(e.on("change", () => this._constructLegendElementsForBuildingSceneLayer()), "sublayers-watcher");
      const l = e.toArray();
      for (const i of l) {
        const e = d(() => ["renderer" in i && i.renderer, i.opacity, i.title, i.visible], () => this._constructLegendElementsForBuildingSceneLayer());
        if (this.addHandles(e, "sublayers-watcher"), !this.respectLayerVisibility || i.visible) {
          const e = null != i?.opacity ? i.opacity : null,
            l = null != e ? e * t : t;
          if ("building-group" === i.type) {
            const e = {
                type: "symbol-table",
                title: i.title,
                infos: []
              },
              t = await this._generateLegendElementsForBuildingSublayers(i.sublayers, l);
            e.infos.push(...t), s = [e, ...s];
          } else if (i.renderer) {
            s = [...(await this._getRendererLegendElements(i.renderer, {
              title: i.title,
              opacity: l,
              sublayer: i
            })), ...s];
          }
        }
      }
      return s.filter(e => !!e && (!("infos" in e) || !e.infos || e.infos.length > 0));
    }
    async _constructLegendElementsForSublayers() {
      this.legendElements = [], this.removeHandles("sublayers-watcher");
      const e = this.layer;
      if (Pe(e) || Ae(e) || De(e)) {
        this.addHandles(d(() => e.sublayers, () => this._constructLegendElementsForSublayers), "sublayers-watcher");
        try {
          this.legendElements = await this._generateLegendElementsForSublayers(e.sublayers, this.opacity), this.notifyChange("ready");
        } catch (t) {
          o.getLogger(this).warn("Request to server for legend has failed!", t);
        }
      }
    }
    async _generateLegendElementsForSublayers(e, t, s) {
      const l = this.layer;
      let i = [];
      this.addHandles(e.on("change", () => this._constructLegendElementsForSublayers()), "sublayers-watcher");
      let n = e.toArray();
      !s && this.sublayerIds && this.sublayerIds.length && (n = De(l) ? this.sublayerIds.map(e => l.findSublayerForSubtypeCode(e)).filter(r) : this.sublayerIds.map(e => l.findSublayerById(e)).filter(r));
      for (const r of n) {
        const e = d(() => [r.renderer, r.opacity, r.title, r.visible, r.legendEnabled], () => this._constructLegendElementsForSublayers());
        if (this.addHandles(e, "sublayers-watcher"), !this.respectLayerVisibility || r.visible && r.legendEnabled && this._isSublayerInScale(r)) {
          const e = null != r?.opacity ? r.opacity : null,
            l = null != e ? e * t : t,
            n = !He(r) || r.originIdOf("renderer") > S.SERVICE && !r.sublayers;
          if (r.renderer && n) {
            await r.load();
            i = [...(await this._getRendererLegendElements(r.renderer, {
              title: r.title,
              opacity: l,
              sublayer: r
            })), ...i];
          } else if (He(r)) {
            const e = await this._generateSymbolTableElementForSublayer(r, l, s);
            e && i.unshift(e);
          }
        }
      }
      return i.filter(e => !!e && (!("infos" in e) || !e.infos || e.infos.length > 0));
    }
    async _generateSymbolTableElementForSublayer(e, t, s) {
      if (!s) {
        s = new Map();
        const t = this.layer,
          l = e.source;
        let i = null;
        if (!(!l || "map-layer" === l.type && l.mapLayerId === e.id && (!l.gdbVersion || l.gdbVersion === ("gdbVersion" in t && t.gdbVersion))) || e.originIdOf("renderer") > S.SERVICE || e.originIdOf("labelingInfo") > S.SERVICE || e.originIdOf("labelsVisible") > S.SERVICE) {
          const e = new v({
            layer: this.layer
          });
          i = e.hasDynamicLayers ? e.dynamicLayers : null, e.destroy();
        }
        const r = i || `${t.uid}-default`;
        (await this._getLegendLayers(r, i)).forEach(e => s.set(e.layerId, e));
      }
      const l = s.get(e.id);
      if ((!l || l?.subLayerIds && l.defaultVisibility) && e.sublayers) {
        const l = await this._generateLegendElementsForSublayers(e.sublayers, t, s);
        return {
          type: "symbol-table",
          title: e.title,
          infos: l
        };
      }
      return this._generateSymbolTableElementForLegendLayer(l, e, t);
    }
    _generateSymbolTableElementForLegendLayer(e, t, s) {
      if (!e?.legend || this.respectLayerVisibility && !this._isLegendLayerInScale(e, t)) return null;
      const l = t?.renderer;
      let i = t?.title || e.layerName;
      if (l && (!t || t?.originIdOf("renderer") > S.SERVICE)) {
        const e = t?.title || this._getRendererTitle(l, t);
        e && (i && "string" != typeof e && "title" in e && (e.title = i), i = e);
      }
      const r = {
          type: "symbol-table",
          title: i,
          legendType: e.legendType || null,
          infos: []
        },
        n = t ? this._sanitizeLegendForSublayer(e.legend.slice(), t) : e.legend;
      return e.legendGroups && e.legendGroups.length > 0 ? e.legendGroups.forEach(t => {
        const l = {
          type: "symbol-table",
          title: t.heading,
          legendType: e.legendType || null,
          infos: this._generateSymbolTableElementInfosForLegendLayer(n.filter(e => e.groupId === t.id), e.layerId, s)
        };
        l.infos?.length > 0 && r.infos.push(l);
      }) : r.infos = this._generateSymbolTableElementInfosForLegendLayer(n, e.layerId, s), r.infos.length > 0 ? r : null;
    }
    _generateSymbolTableElementInfosForLegendLayer(e, t, l) {
      return e.map(e => {
        let i = e.url;
        if (e.imageData && e.imageData.length > 0) i = `data:image/png;base64,${e.imageData}`;else {
          if (0 === i.indexOf("http")) return null;
          i = s(`${this.layer.url}/${t}/images/${i}`);
        }
        return {
          label: e.label,
          src: i,
          opacity: l ?? this.opacity,
          width: e.width,
          height: e.height
        };
      }).filter(r);
    }
    _isSublayerInScale(e) {
      const t = e.minScale || 0,
        s = e.maxScale || 0;
      return !(t > 0 && t < this.scale || s > this.scale);
    }
    _isLegendLayerInScale(e, t) {
      const s = t || this.layer;
      let l = null,
        i = null,
        r = !0;
      return !s.minScale && 0 !== s.minScale || !s.maxScale && 0 !== s.maxScale ? (0 === e.minScale && s.tileInfo && (l = s.tileInfo.lods[0].scale), 0 === e.maxScale && s.tileInfo && (i = s.tileInfo.lods[s.tileInfo.lods.length - 1].scale)) : (l = Math.min(s.minScale, e.minScale) || s.minScale || e.minScale, i = Math.max(s.maxScale, e.maxScale)), (l > 0 && l < this.scale || i > this.scale) && (r = !1), r;
    }
    _sanitizeLegendForSublayer(e, t) {
      if ("version" in this.layer && +this.layer.version < 10.1 || 0 === e.length) return e;
      const s = t.renderer,
        l = e.some(e => e.values);
      let i = 0,
        r = null;
      return l && e.some((e, t) => (e.values || (i = t, r = e, r.label || (r.label = "others")), null != r)), s ? "unique-value" === s.type ? r && (e.splice(i, 1), e.push(r)) : "class-breaks" === s.type && (r && e.splice(i, 1), e.reverse(), r && e.push(r)) : r && (e.splice(i, 1), e.push(r)), e;
    }
    async _getRendererLegendElements(e, t = {}) {
      if (!Te(e, this.view)) return o.getLogger(this).warn(`Renderer of type '${e.type}' not supported!`), [];
      if (Le(e)) return this._constructPointCloudRendererLegendElements(e, t);
      if (Fe(e)) return this._constructDotDensityRendererLegendElements(e);
      const s = await this._loadRenderer(e);
      return Ve(s) ? this._constructPieChartRendererLegendElements(s) : this._constructRendererLegendElements(s, t);
    }
    async _getLegendElementsForFeatureReduction(e) {
      let t = null;
      return "binning" === e.type ? t = e.renderer : "cluster" === e.type && (t = this._getClusterRenderer(e)), t ? this._getRendererLegendElements(t) : [];
    }
    _getPointCloudRendererTitle(e) {
      return (e.legendOptions?.title || e.field) ?? "";
    }
    async _constructPointCloudRendererLegendElements(e, t = {}) {
      const s = t.title,
        l = [];
      let i = null,
        r = null;
      if (Ce(e)) i = {
        type: "symbol-table",
        title: s || this._getPointCloudRendererTitle(e),
        infos: []
      }, e.colorClassBreakInfos.forEach(e => {
        i.infos.unshift({
          label: e.label || e.minValue + " - " + e.maxValue,
          value: [e.minValue, e.maxValue],
          symbol: this._getAppliedCloneSymbol(ye, e.color)
        });
      });else if (Ie(e)) {
        const t = e.stops;
        let l = null;
        if (t?.length && (1 === t.length && (l = t[0].color), !l)) {
          const e = t[0].value,
            s = t[t.length - 1].value;
          if (null != e && null != s) {
            l = U(e + (s - e) / 2, t);
          }
        }
        i = {
          type: "symbol-table",
          title: null,
          infos: [{
            label: null,
            value: null,
            symbol: this._getAppliedCloneSymbol(ye, l || ye.color)
          }]
        };
        const n = k(e.stops ?? []) ?? [];
        r = {
          type: "color-ramp",
          title: s || this._getPointCloudRendererTitle(e),
          infos: n,
          preview: x(n.map(e => e.color), {
            ariaLabel: await $e("previewColorRampAriaLabel")
          })
        };
      } else Re(e) && (i = {
        type: "symbol-table",
        title: s || this._getPointCloudRendererTitle(e),
        infos: []
      }, e.colorUniqueValueInfos?.forEach(e => {
        i.infos.push({
          label: e.label || e.values.join(", "),
          value: e.values.join(", "),
          symbol: this._getAppliedCloneSymbol(ye, e.color)
        });
      }));
      i && i.infos.length && l.push(i), r && r.infos.length && l.push(r);
      const n = l.reduce((e, t) => [...e, ...(t.infos ?? [])], []).filter(e => !!e.symbol).map(t => this._getSymbolPreview(t, this.opacity, {
        symbolConfig: {
          applyColorModulation: !!e.colorModulation
        }
      }));
      return await Promise.allSettled(n), l;
    }
    async _getElementInfoForDotDensity(e, t) {
      const {
          color: s,
          label: l,
          valueExpressionTitle: i
        } = t,
        {
          backgroundColor: r,
          outline: n,
          dotSize: a
        } = e,
        o = this.effectList,
        u = o?.effects.map(e => e.toJSON()),
        c = w(u),
        d = await $e("previewTemplateAriaLabel", l || i),
        y = a + "-" + s + "-" + r + "-" + (n && JSON.stringify(n.toJSON())) + "-" + c,
        h = this._dotDensityUrlCache,
        m = h.has(y) ? h.get(y) : j(e, s, {
          ariaLabel: d
        });
      h.set(y, m);
      const f = {
          shape: {
            type: "image",
            x: 0,
            y: 0,
            width: m.width,
            height: m.height,
            src: m.src
          },
          fill: null,
          stroke: null,
          offset: [0, 0]
        },
        p = D([[f]], [m.width, m.height], {
          effectView: this.effectList,
          ariaLabel: d
        });
      return {
        opacity: 1,
        src: m.src,
        preview: p,
        width: m.width,
        height: m.height
      };
    }
    async _constructDotDensityRendererLegendElements(e) {
      const t = e.calculateDotValue(this.view.scale),
        s = e.legendOptions?.unit,
        l = {
          type: "symbol-table",
          title: {
            value: t && Math.round(t),
            unit: s || ""
          },
          infos: []
        };
      for (const i of e.attributes) {
        const t = await this._getElementInfoForDotDensity(e, i);
        t.label = i.label || i.valueExpressionTitle || i.field, l.infos.push(t);
      }
      return [l];
    }
    async _constructPieChartRendererLegendElements(e) {
      const t = this.layer.opacity,
        s = [];
      let l = null;
      const i = e.outline;
      e.attributes.forEach(e => {
        const t = new se({
            color: e.color,
            outline: i
          }),
          l = e.label || e.valueExpressionTitle || e.field;
        s.push({
          label: l,
          symbol: t
        });
      });
      const r = s.length ? [...s] : [];
      if (e.othersCategory?.color && 0 !== e.othersCategory?.threshold) {
        const t = new se({
          color: e.othersCategory.color,
          outline: i
        });
        l = e.othersCategory.label || "Other", s.push({
          label: l,
          symbol: t
        });
      }
      if (e.defaultColor?.a) {
        const t = new se({
          color: e.defaultColor,
          outline: i
        });
        s.push({
          label: e.defaultLabel,
          symbol: t
        });
      }
      const n = (await this._getVisualVariableLegendElements(e, this.layer)) || [];
      if (s.length) {
        n.unshift({
          type: "symbol-table",
          title: null,
          infos: s
        });
        const t = r.filter(e => e.label !== l).map(e => e.symbol.color).filter(Boolean),
          a = O(t, {
            holePercentage: e.holePercentage,
            backgroundColor: e.backgroundFillSymbol?.color,
            effectList: this.effectList,
            outline: i,
            ariaLabel: await $e("previewPieChartAriaLabel")
          });
        n.unshift({
          type: "pie-chart-ramp",
          title: this._getRendererTitle(e, this.layer),
          infos: s,
          preview: a
        });
      }
      const a = n.reduce((e, t) => [...e, ...this._getAllInfos(t)], []).filter(e => !!e?.symbol && !e?.preview).map(e => this._getSymbolPreview(e, t, {
        effectList: this.effectList
      }));
      return await Promise.allSettled(a), n;
    }
    async _constructRendererLegendElements(e, t = {}) {
      const {
          title: s,
          sublayer: l
        } = t,
        i = l || this.layer;
      this._hasColorRamp = !1, this._hasOpacityRamp = !1, this._hasSizeRamp = !1, this._scaleDrivenSizeVariable = null;
      const r = (await this._getVisualVariableLegendElements(e, i)) || [],
        n = {
          type: "symbol-table",
          title: s || this._getRendererTitle(e, i),
          infos: []
        };
      let a = null,
        o = !1;
      const u = new Set();
      if (me(e) && !this._hasSizeRamp) {
        const t = await K(e);
        n.infos.push({
          label: null,
          symbol: t
        });
      } else if (Ne(e)) {
        let t = s;
        const l = qe(e) ? "univariate-above-and-below-ramp" : "univariate-color-size-ramp",
          i = r.findIndex(e => "color-ramp" === e.type),
          n = -1 !== i ? r.splice(i, 1)[0] : null,
          a = r.findIndex(e => "size-ramp" === e.type),
          o = -1 !== a ? r.splice(a, 1)[0] : null,
          u = [];
        n && (t = n.title, u.push(n)), o && (t = o.title, u.push(o)), u.length > 0 && r.push({
          type: l,
          title: t,
          infos: u
        });
      } else if (ve(e)) {
        const t = q(e);
        r.push({
          type: "heatmap-ramp",
          title: s || this._getRendererTitle(e, i),
          infos: t,
          preview: x(t.map(e => e.color), {
            effectList: this.effectList,
            ariaLabel: await $e("previewColorRampAriaLabel")
          })
        });
      } else if (we(e)) {
        const t = e && e.authoringInfo;
        if (t && "relationship" === t.type) {
          const {
              numClasses: s,
              field1: l,
              field2: a
            } = t,
            o = t.focus;
          if (s && l && a) {
            const t = [l, a];
            let c = H(o) || 0;
            for (const e of t) {
              const {
                  field: t,
                  normalizationField: s,
                  label: l
                } = e,
                r = l || {
                  field: this._getFieldAlias(t, i),
                  normField: s && this._getFieldAlias(s, i)
                },
                a = We.clone();
              a.angle = c, n.infos.push({
                label: r,
                symbol: a
              }), u.add(a), c += 90;
            }
            const d = $({
              focus: o,
              numClasses: s,
              infos: e.uniqueValueInfos ?? []
            });
            r.unshift(d);
          }
        } else if (Me(this.layer) || Ue(this.layer)) e.uniqueValueInfos?.forEach(e => {
          e.symbol && n.infos.push({
            label: e.label || e.value,
            value: e.value,
            symbol: e.symbol
          });
        });else {
          const {
              field: t,
              field2: l,
              field3: r,
              fieldDelimiter: a,
              valueExpression: u,
              defaultSymbol: c
            } = e,
            d = !(!t && !u || !l && !r),
            y = [];
          if (e.uniqueValueGroups?.forEach(e => {
            const s = {
              type: "symbol-table",
              title: e.heading,
              infos: []
            };
            e.classes?.forEach(e => {
              const {
                symbol: n,
                values: o
              } = e;
              if (n) {
                const c = [],
                  y = [];
                for (const e of o ?? []) {
                  const {
                      value: s,
                      value2: n,
                      value3: o
                    } = e,
                    h = [],
                    m = [];
                  (t || u) && (h.push(s), m.push(this._getDomainName(t, s, i))), l && (h.push(n), m.push(this._getDomainName(l, n, i))), r && (h.push(o), m.push(this._getDomainName(r, o, i))), c.push(d ? h.join(a || "") : h[0]), y.push(m.join(" - "));
                }
                const h = c.join(", ");
                let m = e.label;
                if (!m) {
                  const e = y.filter(Boolean);
                  m = e.length ? e.join(", ") : h;
                }
                s.infos.push({
                  label: m,
                  value: h,
                  symbol: n
                });
              }
            }), s.infos.length && y.push(s);
          }), y.length) {
            const t = y[0];
            1 === y.length && "title" in t && !t.title ? n.infos.push(...(t.infos ?? [])) : (c && (y.push({
              type: "symbol-table",
              infos: [{
                label: e.defaultLabel || "others",
                symbol: c
              }]
            }), o = !0), n.infos.push(...y)), s || e.legendOptions?.title || e.valueExpressionTitle || (n.title = null);
          }
        }
        e.defaultSymbol && !o && (n.infos.push({
          label: e.defaultLabel || "others",
          symbol: e.defaultSymbol
        }), o = !0);
      } else if (_e(e)) {
        a = this._isUnclassedRenderer(e);
        (!a || !this._hasSizeRamp) && (e.classBreakInfos.forEach(e => {
          e.symbol && n.infos.unshift({
            label: e.label || (a ? null : e.minValue + " - " + e.maxValue),
            value: [e.minValue, e.maxValue],
            symbol: e.symbol
          });
        }), a && (n.title = null), this._updateInfosforClassedSizeRenderer(e, n.infos)), e.defaultSymbol && !a && (n.infos.push({
          label: e.defaultLabel || "others",
          symbol: e.defaultSymbol
        }), o = !0);
      } else if (ge(e)) {
        if (Ue(this.layer) || ke(this.layer)) {
          const t = await this._constructTileImageryStretchRendererElements(e);
          Be(t) ? r.push(t) : n.infos = t;
        } else {
          const t = this.layer;
          let s, l;
          if (e.statistics?.length) {
            const t = e.statistics[0];
            X(t) ? (s = t.min, l = t.max) : [s, l] = t;
          }
          let i = [],
            a = t.serviceRasterInfo;
          if (t.rasterFunction) try {
            a = await t.generateRasterInfo(t.rasterFunction);
          } catch {}
          const o = C(a.pixelType);
          if (1 === a.bandCount) {
            const i = t.bandIds?.[0] || 0;
            s = null != s ? s : a.statistics ? a.statistics[i].min : o[0], l = null != l ? l : a.statistics ? a.statistics[i].max : o[1], s || l ? r.push(await this._getStretchLegendElements(e, {
              min: s,
              max: l
            })) : this._getServerSideLegend();
          } else if (t.bandIds && 1 === t.bandIds.length) s = null != s ? s : a.statistics ? a.statistics[t.bandIds[0]].min : o[0], l = null != l ? l : a.statistics ? a.statistics[t.bandIds[0]].max : o[1], s || l ? r.push(await this._getStretchLegendElements(e, {
            min: s,
            max: l
          })) : this._getServerSideLegend();else if (a.bandCount >= 3) {
            const {
                bandInfos: e
              } = a,
              {
                bandIds: s
              } = t;
            e.length >= a.bandCount ? 3 === s?.length ? (i = s.map(t => e[t].name), n.infos = this._createSymbolTableElementMultiBand(i)) : "lerc" === t.format ? (i = [0, 1, 2].map(t => e[t].name), n.infos = this._createSymbolTableElementMultiBand(i)) : this._getServerSideLegend() : "lerc" === t.format ? (i = ["band1", "band2", "band3"], n.infos = this._createSymbolTableElementMultiBand(i)) : this._getServerSideLegend();
          } else this._getServerSideLegend();
        }
      } else if (pe(e)) e.colormapInfos.forEach(e => {
        n.infos.push({
          label: e.label,
          value: e.value,
          symbol: this._getAppliedCloneSymbol(he, e.color)
        });
      });else if (Se(e)) {
        let s = e.symbol;
        switch (t.geometryType) {
          case "point":
            s = "pointSymbol" in i ? i.pointSymbol : null;
            break;
          case "polyline":
            s = "lineSymbol" in i ? i.lineSymbol : null;
            break;
          case "polygon":
            s = "polygonSymbol" in i ? i.polygonSymbol : null;
        }
        const l = this._hasClusterSizeVariable && this._getClusterSymbol() || !this._hasSizeRamp;
        e.symbol && l && n.infos.push({
          label: e.label,
          symbol: s
        });
      } else if (fe(e)) {
        e.outputUnit && (this.title = "(" + e.toJSON().outputUnit + ")"), n.title = e.attributeField;
        const t = e.getClassBreakInfos();
        t?.length ? t.forEach(e => {
          n.infos.push({
            label: e.minValue + " - " + e.maxValue,
            symbol: e.symbol
          });
        }) : n.infos.push({
          label: e.attributeField,
          symbol: e.getDefaultSymbol()
        });
      } else be(e) && r.push(await this._getStretchLegendElements(e, {
        min: 0,
        max: 255
      }));
      const c = e.defaultSymbol;
      !c || o || Se(e) || a && !this._hasColorRamp && !this._hasSizeRamp && !this._hasOpacityRamp || r.push({
        type: "symbol-table",
        infos: [{
          label: e.defaultLabel || "others",
          symbol: c
        }]
      }), n.infos.length && r.unshift(n);
      const d = null == t.opacity ? this.opacity : t.opacity,
        y = this._isTallSymbol("visualVariables" in e ? e.visualVariables : null),
        h = Me(this.layer) || Ue(this.layer),
        m = r.reduce((e, t) => [...e, ...this._getAllInfos(t)], []).filter(e => !!e?.symbol).filter(e => {
          if ("cim" === e.symbol.type) {
            const {
              minScale: t,
              maxScale: s
            } = e.symbol.data;
            if (t && t < this.scale || s && s > this.scale) return !1;
          }
          return !0;
        }).map(e => this._getSymbolPreview(e, d, {
          isDefault: e.symbol === c,
          applyScaleDrivenSize: !u.has(e.symbol),
          symbolConfig: {
            isTall: y,
            isSquareFill: h
          },
          effectList: u.has(e.symbol) ? null : this.effectList
        }));
      return e = null, await Promise.allSettled(m), r;
    }
    _getServerSideLegend() {
      setTimeout(() => this.buildLegendElementsForTools(), 0);
    }
    _getAllInfos(e) {
      const t = e?.infos;
      return t ? t.reduce((e, t) => e.concat(this._getAllInfos(t)), []) : [e];
    }
    async _constructTileImageryStretchRendererElements(e) {
      const t = this.layer,
        s = t.symbolizer.rasterInfo ?? t.raster.rasterInfo;
      let l, i;
      const r = e?.statistics?.length ? e.statistics : s?.statistics;
      if (r) {
        const e = r[0];
        X(e) ? (l = e.min, i = e.max) : [l, i] = e;
      } else {
        const e = C(s.pixelType);
        l = e[0], i = e[1];
      }
      if (t.hasStandardTime() && (l = t.getStandardTimeValue(l), i = t.getStandardTimeValue(i)), 1 === s.bandCount || 1 === t.bandIds?.length) return this._getStretchLegendElements(e, {
        min: l,
        max: i
      });
      const n = (t?.bandIds?.length ? t.bandIds : Array.from(Array(Math.min(s.bandCount, 3)).keys())).map(e => s.bandInfos[e].name);
      return n.length < 3 ? n.push(n[1]) : n.length > 3 && n.splice(3), this._createSymbolTableElementMultiBand(n);
    }
    async _getStretchLegendElements(e, t) {
      const s = e.colorRamp,
        l = B(s, t);
      return {
        type: "stretch-ramp",
        title: "",
        infos: l,
        preview: x(l.map(e => e.color), {
          ariaLabel: await $e("previewColorRampAriaLabel")
        })
      };
    }
    _getClusterSymbol() {
      const e = this.layer,
        t = "featureReduction" in e && e.featureReduction,
        s = t && "symbol" in t && t.renderer;
      return s && !0 !== s?.authoringInfo?.isAutoGenerated ? null : t && "symbol" in t ? t.symbol : null;
    }
    async _getSizeLegendElement(e, t, s, l) {
      return {
        type: "size-ramp",
        title: this._hasClusterSizeVariable ? this._getClusterTitle(t) : e,
        infos: await W(s, t, await Y(s), this.scale, this.view, l, this._hasClusterSizeVariable ? this._getClusterSymbol() : null)
      };
    }
    _createSymbolTableElementMultiBand(e) {
      const t = [],
        s = ["red", "green", "blue"];
      return e.forEach((e, l) => {
        t.push({
          label: {
            colorName: s[l],
            bandName: e
          },
          src: ee[l],
          opacity: this.opacity ?? 1
        });
      }), t;
    }
    _updateInfosforClassedSizeRenderer(e, t) {
      const s = e.authoringInfo && "class-breaks-size" === e.authoringInfo.type,
        l = e.classBreakInfos.some(e => A(e.symbol));
      if (s && l) {
        const s = G,
          l = J,
          i = e.classBreakInfos.length,
          r = (s - l) / (i > 1 ? i - 1 : i);
        t.forEach((e, t) => {
          e.size = s - r * t;
        });
      }
    }
    _isTallSymbol(e) {
      let t = !1,
        s = !1;
      if (e) for (let l = 0; l < e.length && (!t || !s); l++) {
        const i = e[l];
        "size" === i.type && ("height" === i.axis && (t = !0), "width-and-depth" === i.axis && (s = !0));
      }
      return t && s;
    }
    async _getSymbolPreview(e, t, s) {
      let l = !s?.isDefault && null == e.size && this._hasSizeRamp ? m(z.size) : e.size;
      if (this._scaleDrivenSizeVariable && s?.applyScaleDrivenSize) {
        const {
          getSize: t
        } = await import("../../../renderers/visualVariables/support/visualVariableUtils.js");
        l = t(this._scaleDrivenSizeVariable, null, {
          view: this.view.type,
          scale: this.scale,
          shape: "simple-marker" === e.symbol.type ? e.symbol.style : null
        });
      }
      const i = !s?.isDefault && this._hasSizeRamp || !(!this._scaleDrivenSizeVariable || !s?.applyScaleDrivenSize);
      return P(e.symbol, {
        size: l,
        opacity: t,
        scale: !1,
        symbolConfig: s?.symbolConfig,
        effectView: s?.effectList,
        style: "legend",
        cimOptions: {
          allowScalingUp: i,
          viewParams: this.isScaleDriven ? {
            viewingMode: "2d" === this.view?.type ? "map" : this.view?.viewingMode,
            scale: this.view?.scale
          } : null
        },
        ariaLabel: e.label && "string" != typeof e.label ? null : await $e("previewTemplateAriaLabel", e.label)
      }).then(t => (e.preview = t, e)).catch(() => (e.preview = null, e));
    }
    _getClusterRenderer(e) {
      this._hasClusterSizeVariable = !1;
      const t = "renderer" in this.layer ? this.layer.renderer : null,
        s = e.renderer?.clone() || t?.clone(),
        l = M(this.layerView._effectiveRenderer, this.view);
      if (l && null != s && "visualVariables" in s) {
        const t = s.visualVariables?.some(e => "size" === e.type && "outline" !== e.target && !ce.test(e.valueExpression));
        if (!t) {
          if ("clusterMinSize" in e && "clusterMaxSize" in e) {
            const {
              clusterMinSize: t,
              clusterMaxSize: s
            } = e;
            l.legendOptions = new F({
              showLegend: t !== s
            });
          }
          const t = s.visualVariables || [];
          s.visualVariables = t.concat([l]), this._hasClusterSizeVariable = !0;
        }
      }
      return s;
    }
    async _loadRenderer(e) {
      const t = [],
        s = e.clone(),
        l = await Y(s);
      if (_e(s) || we(s)) {
        const e = (s.classBreakInfos || s.uniqueValueInfos).map(e => this._fetchSymbol(e.symbol, l).then(t => {
          e.symbol = t;
        }).catch(() => {
          e.symbol = null;
        }));
        Array.prototype.push.apply(t, e);
      }
      return t.push(this._fetchSymbol(s.symbol || s.defaultSymbol, s.defaultSymbol ? null : l).then(e => {
        this._applySymbolToRenderer(s, e, Se(s));
      }).catch(() => {
        this._applySymbolToRenderer(s, null, Se(s));
      })), await Promise.allSettled(t), s;
    }
    _applySymbolToRenderer(e, t, s) {
      s ? e.symbol = t : e.defaultSymbol = t;
    }
    async _fetchSymbol(e, t) {
      if (!e) throw new Error();
      if ("web-style" === e.type) {
        const s = this._webStyleSymbolCache;
        try {
          const l = await ("2d" === this.view.type ? e.fetchCIMSymbol({
            cache: s
          }) : e.fetchSymbol({
            cache: s
          }));
          return this._getAppliedCloneSymbol(l, t);
        } catch {
          throw o.getLogger(this).warn("Fetching web-style failed!"), new Error();
        }
      }
      return this._getAppliedCloneSymbol(e, t);
    }
    _getAppliedCloneSymbol(e, s) {
      if (!e || !s) return e;
      const l = e.clone(),
        i = s && s.toRgba();
      return l.type.includes("3d") ? this._applyColorTo3dSymbol(l, i) : "cim" === l.type ? T(l, s) : l.color && (l.color = new t(i || l.color)), l;
    }
    _applyColorTo3dSymbol(e, s) {
      s && e.symbolLayers.forEach(e => {
        e && (e.material || (e.material = {}), e.material.color = new t(s));
      });
    }
    async _getVisualVariableLegendElements(e, t) {
      if (!("visualVariables" in e) || !e.visualVariables || "vector-field" === e.type) return null;
      const s = e.visualVariables,
        l = [],
        i = [],
        n = [];
      for (const r of s) "color" === r.type ? l.push(r) : "size" === r.type ? i.push(r) : "opacity" === r.type && n.push(r);
      const a = [...l, ...i, ...n];
      let o, u;
      if (0 === l.length && _e(e) && e.classBreakInfos && 1 === e.classBreakInfos.length) {
        const t = e.classBreakInfos[0];
        o = t && t.symbol;
      }
      if (0 === l.length && Se(e) && (o = e.symbol), o) if (o.type.includes("3d")) {
        const e = o.symbolLayers.at(0);
        "water" === e.type ? null != e.color && (u = e.color) : null != e.material?.color && (u = e.material.color);
      } else o.url || (u = o.color);
      const c = this.effectList;
      return (await Promise.all(a.map(async s => {
        if (!s.legendOptions || !1 !== s.legendOptions.showLegend) {
          const l = me(e) ? s.field : this._getRampTitle(s, t);
          let i = null;
          const r = this._getDateFormatOptions(t, s);
          if ("color" === s.type) {
            const e = (await N(s, null, r)) ?? [];
            i = {
              type: "color-ramp",
              title: l,
              infos: e,
              preview: x(e.map(e => e.color), {
                effectList: c,
                ariaLabel: await $e("previewColorRampAriaLabel")
              })
            }, this._hasColorRamp || (this._hasColorRamp = e.length > 0);
          } else if ("size" === s.type && "outline" !== s.target) ce.test(s.valueExpression) ? this._hasClusterSizeVariable || (this._scaleDrivenSizeVariable = s) : (i = await this._getSizeLegendElement(l, s, e, r), this._hasSizeRamp || (this._hasSizeRamp = !(null == i.infos || !i.infos.length)));else if ("opacity" === s.type) {
            const e = (await N(s, u, r)) ?? [];
            i = {
              type: "opacity-ramp",
              title: l,
              infos: e,
              preview: x(e.map(e => e.color), {
                effectList: c,
                ariaLabel: await $e("previewColorRampAriaLabel")
              })
            }, this._hasOpacityRamp || (this._hasOpacityRamp = e.length > 0);
          }
          return i && i.infos ? i : null;
        }
      }))).filter(r);
    }
    _getFieldInfo(e, t) {
      if (!t.field) return null;
      if ("featureReduction" in e) switch (e.featureReduction?.type) {
        case "cluster":
        case "binning":
          {
            const s = e.featureReduction.fields.find(({
              name: e
            }) => e.toLowerCase() === t.field.toLowerCase());
            return s && "getField" in e ? e.getField(s.onStatisticField) : null;
          }
      }
      return "getField" in e ? e.getField?.(t.field) : null;
    }
    _getDateFormat(e, t) {
      const s = "popupTemplate" in e ? e.popupTemplate?.fieldInfos : null;
      if (s?.length && t) return s.find(e => e.fieldName?.toLowerCase() === t.toLowerCase())?.format?.dateFormat;
    }
    _getDateFormatOptions(e, t) {
      const s = this._getFieldInfo(e, t);
      if (!s || !V(s) && !L(s)) return null;
      let l = this._getDateFormat(e, s.name);
      if (!l && "date" === s.type) {
        let e = 0,
          s = 0;
        t.stops ? (e = t.stops?.at(0)?.value ?? e, s = t.stops?.at(-1)?.value ?? s) : "minDataValue" in t && "maxDataValue" in t && (e = t.minDataValue ?? e, s = t.maxDataValue ?? s), l = s - e > 2 * f.days ? "short-date" : "short-date-short-time";
      }
      return {
        fieldType: s.type,
        format: l,
        timeZoneOptions: {
          layerTimeZone: "preferredTimeZone" in e ? e.preferredTimeZone : null,
          viewTimeZone: this.view.timeZone,
          datesInUnknownTimezone: "datesInUnknownTimezone" in e && e.datesInUnknownTimezone
        }
      };
    }
    _getDomainName(e, t, s) {
      if (e && "function" != typeof e) {
        const l = "getField" in s && s.getField?.(e),
          i = l && "getFieldDomain" in s && s.getFieldDomain ? s.getFieldDomain(l.name) : null;
        return "coded-value" === i?.type ? i.getName(t) : null;
      }
      return null;
    }
    _getClusterTitle(e) {
      const t = this.layer,
        s = e.field;
      if ("featureReduction" in t && t.featureReduction && "cluster" === t.featureReduction.type) {
        const e = t.featureReduction,
          l = "popupTemplate" in e && e.popupTemplate,
          i = l && l.fieldInfos;
        if (i) for (const t of i) if (t.fieldName === s) return "cluster_count" === s ? t.label || {
          showCount: !0
        } : t.label;
      }
      return {
        showCount: !0
      };
    }
    _getRampTitle(e, t) {
      let s = e.field,
        l = e.normalizationField,
        i = !1,
        r = !1,
        n = !1,
        a = null;
      s = "function" == typeof s ? null : s, l = "function" == typeof l ? null : l;
      const o = e.legendOptions?.title;
      if (null != o) a = o;else if (e.valueExpressionTitle) a = e.valueExpressionTitle;else {
        if ("renderer" in t && t.renderer && "authoringInfo" in t.renderer && t.renderer.authoringInfo?.visualVariables) {
          const e = t.renderer.authoringInfo.visualVariables;
          for (let t = 0; t < e.length; t++) {
            const s = e[t];
            if ("color" === s.type) {
              if ("ratio" === s.style) {
                i = !0;
                break;
              }
              if ("percent" === s.style) {
                r = !0;
                break;
              }
              if ("percent-of-total" === s.style) {
                n = !0;
                break;
              }
            }
          }
        }
        a = {
          field: s && this._getFieldAlias(s, t),
          normField: l && this._getFieldAlias(l, t),
          ratio: i,
          ratioPercent: r,
          ratioPercentTotal: n
        };
      }
      return a;
    }
    _getRendererTitle(e, t) {
      const s = e;
      if (s.legendOptions?.title) return s.legendOptions.title;
      if (s.valueExpressionTitle) return s.valueExpressionTitle;
      let l = s.field,
        i = null,
        r = null;
      if (_e(s) && (i = s.normalizationField, r = "percent-of-total" === s.normalizationType), l = "function" == typeof l ? null : l, i = "function" == typeof i ? null : i, we(s)) {
        const {
          field2: e,
          field3: i,
          fieldDelimiter: r
        } = s;
        let n = l && this._getFieldAlias(l, t);
        return e && (n = `<${n}>${r}<${this._getFieldAlias(e, t)}>`, i && (n = `${n}${r}<${this._getFieldAlias(i, t)}>`)), n;
      }
      let n = null;
      return (l || i) && (n = {
        field: l && this._getFieldAlias(l, t),
        normField: i && this._getFieldAlias(i, t),
        normByPct: r
      }), n;
    }
    _getFieldAlias(e, t) {
      const s = "popupTemplate" in t ? t.popupTemplate : null,
        l = s?.fieldInfos;
      let i;
      l && l.some(t => e === t.fieldName && (i = t, !0));
      let r = null;
      "getField" in t && t.getField ? r = t.getField(e) : "fieldsIndex" in t && t.fieldsIndex && (r = t.fieldsIndex.get(e));
      let n = null;
      const a = "featureReduction" in t && t.featureReduction;
      a && (!i && "popupTemplate" in a && a.popupTemplate?.fieldInfos && a.popupTemplate.fieldInfos.some(t => e?.toLowerCase() === t.fieldName?.toLowerCase() && (i = t, !0)), "fields" in a && a.fields && (n = a.fields.find(t => t.name?.toLowerCase() === e?.toLowerCase())));
      const o = i || r || n;
      let u = null;
      return o && (u = i?.label || r?.alias || n?.alias || "name" in o && o.name || "fieldName" in o && o.fieldName || null), u;
    }
    _isUnclassedRenderer(e) {
      const t = e.visualVariables;
      let s = !1;
      return _e(e) && e.classBreakInfos && 1 === e.classBreakInfos.length && t && (s = e.field ? t.some(t => !(!t || e.field !== t.field || (e.normalizationField || t.normalizationField) && e.normalizationField !== t.normalizationField)) : !!t.length), s;
    }
  };
e([g()], Je.prototype, "children", void 0), e([g({
  readOnly: !0
})], Je.prototype, "effectList", null), e([g()], Je.prototype, "layerView", void 0), e([g()], Je.prototype, "layer", void 0), e([g()], Je.prototype, "legendElements", void 0), e([g({
  readOnly: !0
})], Je.prototype, "opacity", null), e([g()], Je.prototype, "parent", void 0), e([g({
  readOnly: !0,
  dependsOn: []
})], Je.prototype, "ready", null), e([g()], Je.prototype, "hideLayersNotInCurrentView", void 0), e([g()], Je.prototype, "keepCacheOnDestroy", void 0), e([g()], Je.prototype, "respectLayerVisibility", void 0), e([g({
  readOnly: !0
})], Je.prototype, "scale", null), e([g()], Je.prototype, "sublayerIds", void 0), e([g({
  readOnly: !0
})], Je.prototype, "isScaleDriven", null), e([g()], Je.prototype, "title", void 0), e([g({
  readOnly: !0,
  dependsOn: ["ready"],
  value: 0
})], Je.prototype, "version", null), e([g()], Je.prototype, "view", void 0), Je = e([b("esri.widgets.Legend.support.ActiveLayerInfo")], Je);
const Qe = Je;
export { Qe as default };